import {buildTourProgressElement, postJson} from './product-tours.js'

export const IVR_WITHDRAWAL_TOUR_ID = 'nurse_ivr_withdrawal'
export const IVR_WITHDRAWAL_TOUR_EVENT = `cpm:tour:${IVR_WITHDRAWAL_TOUR_ID}`
const notificationSelector = '[data-tour="notifications"]'
const exampleSlotSelector = '[data-tour-notification-example-slot]'
const notificationRowClass = 'w-full text-left px-4 py-3 hover:bg-gray-50 transition flex items-start gap-3'
let activeTour = null

function buildContent(documentObject, index) {
    const content = documentObject.createElement('div')
    content.append(buildTourProgressElement(index, 2, documentObject))

    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = index === 0
        ? 'If one of your patients withdraws through the automated phone line (IVR), CPM alerts you here. You do not need to check for it. Click Notifications to see an example.'
        : 'The alert names the patient and confirms they were removed from your case load. Select it to mark it read and open the patient overview. This example is not real, so nothing changes.'
    content.append(body)

    return content
}

function buildExampleAlert(documentObject) {
    const alert = documentObject.createElement('button')
    alert.type = 'button'
    alert.className = notificationRowClass
    alert.setAttribute('data-tour-ivr-example', '')
    alert.setAttribute('aria-label', 'Example IVR withdrawal notification. Select to finish the tour.')

    const content = documentObject.createElement('div')
    content.className = 'flex-1 min-w-0'

    const meta = documentObject.createElement('div')
    meta.className = 'flex items-center gap-1.5 mb-0.5'
    const source = documentObject.createElement('span')
    source.className = 'px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600'
    source.textContent = 'CPM'
    const time = documentObject.createElement('span')
    time.className = 'ml-auto text-[10px] text-gray-400 shrink-0'
    time.textContent = 'Just now'
    meta.append(source, time)

    const headline = documentObject.createElement('div')
    headline.className = 'text-sm text-gray-800'
    headline.textContent = 'Sample patient withdrew using the automated phone line and was removed from your case load'

    const preview = documentObject.createElement('div')
    preview.className = 'text-[12px] text-gray-500 line-clamp-2 mt-0.5'
    preview.textContent = 'Example alert · Select to finish the tour'

    const unread = documentObject.createElement('span')
    unread.className = 'mt-1 w-2 h-2 rounded-full bg-pink-600 shrink-0'
    unread.setAttribute('aria-label', 'unread')

    content.append(meta, headline, preview)
    alert.append(content, unread)

    return alert
}

function buildContextAlert(documentObject, {source, badgeClass, headline}) {
    const row = documentObject.createElement('div')
    row.className = notificationRowClass
    row.setAttribute('data-tour-ivr-context', '')

    const content = documentObject.createElement('div')
    content.className = 'flex-1 min-w-0'
    const meta = documentObject.createElement('div')
    meta.className = 'flex items-center gap-1.5 mb-0.5'
    const badge = documentObject.createElement('span')
    badge.className = `px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${badgeClass}`
    badge.textContent = source
    const time = documentObject.createElement('span')
    time.className = 'ml-auto text-[10px] text-gray-400 shrink-0'
    time.textContent = 'Earlier'
    const title = documentObject.createElement('div')
    title.className = 'text-sm text-gray-800'
    title.textContent = headline
    meta.append(badge, time)
    content.append(meta, title)
    row.append(content)

    return row
}

export function startIvrWithdrawalTour(browserWindow, documentObject, ShepherdLibrary) {
    if (activeTour || ShepherdLibrary.activeTour) {
        return false
    }

    const notification = documentObject.querySelector(notificationSelector)
    const dropdown = notification?.querySelector('.dropdown-container')
    const trigger = dropdown?.querySelector('button')
    const menu = dropdown?.querySelector('[role="menu"]')
    const slot = menu?.querySelector(exampleSlotSelector)
    const list = menu?.querySelector('[data-tour-notification-list]')
    const footer = menu?.querySelector('[data-tour-notification-footer]')
    const headerAction = menu?.querySelector('[data-tour-notification-header-action]')
    const headerSummary = menu?.querySelector('[data-tour-notification-summary]')
    const dropdownState = dropdown && browserWindow.Alpine?.$data(dropdown)

    if (!trigger || !menu || !slot || !list || !footer || !dropdownState) {
        return false
    }

    dropdownState.open = false
    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            modalOverlayOpeningPadding: 10,
            modalOverlayOpeningRadius: 16,
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        useModalOverlay: true,
    })
    activeTour = tour
    let awaitingNotificationClick = true
    let example = null
    let contextAlerts = []
    let advanceTimer = null
    const triggerWasDisabled = trigger.disabled
    const listWasHidden = list.hidden
    const footerWasHidden = footer.hidden
    const headerActionWasHidden = headerAction?.hidden
    const headerSummaryText = headerSummary?.textContent

    const closeExample = () => {
        if (advanceTimer !== null) {
            browserWindow.clearTimeout(advanceTimer)
            advanceTimer = null
        }
        example?.remove()
        example = null
        contextAlerts.forEach(alert => alert.remove())
        contextAlerts = []
        slot.scrollTop = 0
        trigger.disabled = triggerWasDisabled
        list.hidden = listWasHidden
        footer.hidden = footerWasHidden
        if (headerAction) headerAction.hidden = headerActionWasHidden
        if (headerSummary) headerSummary.textContent = headerSummaryText
        dropdownState.open = false
    }

    const openExample = () => {
        if (!awaitingNotificationClick || activeTour !== tour) {
            return
        }
        awaitingNotificationClick = false
        dropdownState.open = true
        trigger.disabled = true

        contextAlerts = [
            {source: 'Forum', badgeClass: 'bg-blue-50 text-blue-700', headline: 'Example discussion reply'},
            {source: 'Announcement', badgeClass: 'bg-amber-50 text-amber-700', headline: 'Example team announcement'},
            {source: 'Practice', badgeClass: 'bg-purple-50 text-purple-700', headline: 'Example practice update'},
        ].map(alert => buildContextAlert(documentObject, alert))
        example = buildExampleAlert(documentObject)
        example.addEventListener('click', event => {
            event.preventDefault()
            event.stopPropagation()
            tour.complete()
        })
        slot.append(...contextAlerts, example)
        list.hidden = true
        if (headerAction) headerAction.hidden = true
        if (headerSummary) headerSummary.textContent = 'Sample notifications'

        tour.hide()
        // Let the dropdown settle before Shepherd measures its anchor.
        advanceTimer = browserWindow.setTimeout(() => {
            advanceTimer = null
            if (activeTour === tour && example?.isConnected) {
                slot.scrollTop = slot.scrollHeight
                tour.next()
            }
        }, 280)
    }
    trigger.addEventListener('click', openExample)

    tour.addStep({
        id: 'ivr-withdrawal-notifications',
        attachTo: {element: trigger, on: 'bottom-end'},
        canClickTarget: true,
        buttons: [
            {text: 'Not now', classes: 'shepherd-button-secondary', action: () => tour.cancel()},
            {text: 'Open Notifications', action: () => browserWindow.requestAnimationFrame(() => {
                if (activeTour === tour && awaitingNotificationClick) trigger.click()
            })},
        ],
        title: 'Open Notifications',
        text: buildContent(documentObject, 0),
    })
    tour.addStep({
        id: 'ivr-withdrawal-example',
        attachTo: {
            element: () => browserWindow.innerWidth < 900 ? menu : example,
            on: browserWindow.innerWidth < 900 ? 'bottom-end' : 'left',
        },
        canClickTarget: true,
        buttons: [
            {text: 'Back', classes: 'shepherd-button-secondary', action: () => {
                closeExample()
                awaitingNotificationClick = true
                tour.back()
            }},
            {text: 'Select example', action: () => example?.click()},
        ],
        title: 'Select the example alert',
        text: buildContent(documentObject, 1),
    })

    const dismissFromOverlay = event => {
        if (event.target.closest?.('.shepherd-modal-overlay-container')) {
            tour.cancel()
        }
    }
    documentObject.addEventListener('click', dismissFromOverlay, true)

    const cleanup = () => {
        documentObject.removeEventListener('click', dismissFromOverlay, true)
        trigger.removeEventListener('click', openExample)
        closeExample()
        if (activeTour === tour) {
            activeTour = null
        }
    }

    tour.on('complete', () => {
        cleanup()
        postJson(browserWindow, `/tours/${IVR_WITHDRAWAL_TOUR_ID}/complete`)
    })
    tour.on('cancel', () => {
        cleanup()
        postJson(browserWindow, `/tours/${IVR_WITHDRAWAL_TOUR_ID}/dismiss`)
    })

    tour.start()

    return true
}

export function registerIvrWithdrawalTour(browserWindow, documentObject, ShepherdLibrary) {
    const start = () => startIvrWithdrawalTour(browserWindow, documentObject, ShepherdLibrary)
    browserWindow.addEventListener(IVR_WITHDRAWAL_TOUR_EVENT, start)

    return () => browserWindow.removeEventListener(IVR_WITHDRAWAL_TOUR_EVENT, start)
}
